/**
 * 約定履歴ティッカーの推定エリア・売買区分の表示値 (AC-BIN-031)。
 *
 * 値は eSLJ が板情報との突合で推定したものを backend がそのまま渡してくる (eBalance では
 * 推定し直さない)。 ここでは 「推定中」 / 特定できない 「—」 / 値 のどれを出すかだけを決める。
 */
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type {
  BiddingIntradayMarketAreaGroup,
  BiddingMarketContract,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_market_model_pb'

import {
  MARKET_AREA_GROUPS,
  type MarketAreaGroup,
} from '~/pages/IntradayBidding/models/marketBoardPivot'

/** 推定前・特定できない・値 の 3 つ。 値は買 / 売 のどちらか (Paid = 買、 Given = 売)。 */
export type TickerEstimatedSide =
  | { kind: 'estimating' }
  | { kind: 'unidentified' }
  | { kind: 'identified'; side: Side.BUY | Side.SELL }

export type TickerEstimatedArea =
  | { kind: 'estimating' }
  | { kind: 'unidentified' }
  | { kind: 'identified'; areaGroup: MarketAreaGroup }

export type TickerEstimate = {
  side: TickerEstimatedSide
  sellArea: TickerEstimatedArea
  buyArea: TickerEstimatedArea
}

const ESTIMATING: TickerEstimate = {
  side: { kind: 'estimating' },
  sellArea: { kind: 'estimating' },
  buyArea: { kind: 'estimating' },
}

const isMarketAreaGroup = (
  g: BiddingIntradayMarketAreaGroup,
): g is MarketAreaGroup =>
  (MARKET_AREA_GROUPS as readonly BiddingIntradayMarketAreaGroup[]).includes(g)

/** 未設定 = 特定できない。 未知の値は undefined (= 取得できない扱い)。 */
const estimatedAreaOf = (
  g: BiddingIntradayMarketAreaGroup | undefined,
): TickerEstimatedArea | undefined => {
  if (g === undefined) return { kind: 'unidentified' }
  return isMarketAreaGroup(g) ? { kind: 'identified', areaGroup: g } : undefined
}

/** 未設定 = 特定できない。 未知の値は undefined (= 取得できない扱い)。 */
const estimatedSideOf = (
  side: Side | undefined,
): TickerEstimatedSide | undefined => {
  switch (side) {
    case undefined:
      return { kind: 'unidentified' }
    case Side.BUY:
    case Side.SELL:
      return { kind: 'identified', side }
    default:
      return undefined
  }
}

/**
 * 約定 1 件の推定の表示値。 推定結果が無い間 (推定前・eSLJ から取得できない間) は 3 項目とも
 * 「推定中」。 未知の値 (BFF が UNSPECIFIED に落とした値) を含む推定も取得できない扱いにして
 * 行全体を 「推定中」 にする (「—」 = 特定できなかった、 と断定しない)。
 */
export function tickerEstimateOf(
  contract: BiddingMarketContract,
): TickerEstimate {
  const estimation = contract.estimation
  if (estimation === undefined) return ESTIMATING
  const side = estimatedSideOf(estimation.side)
  const sellArea = estimatedAreaOf(estimation.sellAreaGroup)
  const buyArea = estimatedAreaOf(estimation.buyAreaGroup)
  if (side === undefined || sellArea === undefined || buyArea === undefined) {
    return ESTIMATING
  }
  return { side, sellArea, buyArea }
}
